/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    Tooltip,
    Chip,
} from "@mui/material";
import {
    Close,
    AccountBalanceWallet,
    AccessAlarm,
    TrendingUp,
    Handshake,
    OpenInNew,
    WarningAmber,
    CheckCircleOutline,
    NorthEast,
} from "@mui/icons-material";

import {
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
} from "../styles";
import { colors } from "../../../utility/types/constant";
import {
    IKpiBreakdown,
    IKpiBreakdownItem,
    IKpiPayerHistory,
    KpiBreakdownMetric,
    CollectionsRange,
    CustomerSegment,
    CUSTOMER_SEGMENT_LABELS,
} from "../../../utility/types/collectTypes";
import {
    recoveryStateLabel,
    recoveryStateColors,
} from "../recoveryStateHelpers";
import { submitGetMetricBreakdown } from "../../../redux/services/collectService";

const FONT = "'Montserrat', sans-serif";

interface Props {
    /** The metric to drill into, or null when the modal is closed. */
    metric: KpiBreakdownMetric | null;
    businessId: string | null;
    range: CollectionsRange;
    /** Fallback currency while the breakdown is loading. */
    currency?: string;
    onClose: () => void;
}

// ── Per-metric presentation config ───────────────────────────────────────
const METRIC_META: Record<
    KpiBreakdownMetric,
    {
        title: string;
        subtitle: string;
        accent: string;
        icon: React.ReactNode;
    }
> = {
    outstanding: {
        title: "Outstanding A/R",
        subtitle:
            "Every open invoice still owed to you highest balances first. Sorted so you can act where the money is.",
        accent: colors.primary,
        icon: <AccountBalanceWallet />,
    },
    overdue: {
        title: "Overdue invoices",
        subtitle:
            "Invoices past their due date that still owe a balance, your most time-sensitive collections.",
        accent: "#dc2626",
        icon: <AccessAlarm />,
    },
    collected: {
        title: "Collected payments",
        subtitle:
            "QuickBooks cash payments applied to invoices during the selected period.",
        accent: colors.mainSecondary,
        icon: <TrendingUp />,
    },
    promises: {
        title: "Promise-to-Pay",
        subtitle:
            "Active payment commitments from your customers, the cash you're expecting next.",
        accent: colors.blueRibbon,
        icon: <Handshake />,
    },
};

// ── Payer history → human "good / bad payer" rating ──────────────────────
const SEGMENT_TONE: Record<CustomerSegment, { bg: string; fg: string }> = {
    new: { bg: "#eff6ff", fg: "#1d4ed8" },
    reliable: { bg: "#ecfdf5", fg: "#0f7a5b" },
    occasional_late: { bg: "#fffbeb", fg: "#b45309" },
    at_risk: { bg: "#fff7ed", fg: "#c2410c" },
    delinquent: { bg: "#fef2f2", fg: "#b91c1c" },
    unresponsive: { bg: "#fef2f2", fg: "#b91c1c" },
    disputer: { bg: "#fef2f2", fg: "#b91c1c" },
};

function formatCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    } catch {
        return `$${(amount || 0).toFixed(2)}`;
    }
}

function formatDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        });
    } catch {
        return value;
    }
}

function daysOverdueFrom(dueDate?: string, fallback?: number): number {
    if (typeof fallback === "number" && fallback > 0) return fallback;
    if (!dueDate) return 0;
    const ms = Date.now() - new Date(dueDate).getTime();
    return Math.max(0, Math.floor(ms / 86400000));
}

/** Build a compact payer-history chip + tooltip from the cached profile. */
function PayerHistoryCell({
    payer,
}: {
    payer?: IKpiPayerHistory | null;
}): JSX.Element {
    if (!payer || !payer.segment) {
        return (
            <Typography
                sx={{ fontFamily: FONT, fontSize: "0.78rem", color: "#9ca3af" }}
            >
                No history yet
            </Typography>
        );
    }

    const tone = SEGMENT_TONE[payer.segment] || SEGMENT_TONE.new;
    const label = CUSTOMER_SEGMENT_LABELS[payer.segment] || payer.segment;

    // The on-time rate is only meaningful once the customer has actually
    // settled invoices that had a due date. With no settled history the
    // stored rate defaults to 100% ("benefit of the doubt"), which would be
    // misleading to show next to an at-risk / delinquent badge — so we only
    // surface the percentage when there's real payment history behind it.
    const settledWithDueDate =
        (payer.onTimePayments ?? 0) + (payer.latePayments ?? 0);
    const hasOnTimeHistory = settledWithDueDate > 0;
    const onTimePct =
        hasOnTimeHistory && typeof payer.onTimeRate === "number"
            ? Math.round(payer.onTimeRate * 100)
            : null;
    const settled = payer.invoicesPaid ?? 0;
    const brokenPromises = payer.promisesBroken ?? 0;

    const avgDays =
        hasOnTimeHistory && typeof payer.avgDaysToPay === "number"
            ? Math.round(payer.avgDaysToPay)
            : null;
    const paceLabel =
        avgDays === null
            ? null
            : avgDays <= 0
                ? `Pays ${Math.abs(avgDays)}d early on avg`
                : `Pays ${avgDays}d late on avg`;

    // Supporting line under the segment chip — prefer the most informative
    // truthful signal we have.
    let subLine: { text: string; color: string } | null = null;
    if (onTimePct !== null) {
        subLine = { text: `${onTimePct}% on time`, color: "#6b7280" };
    } else if (brokenPromises > 0) {
        subLine = {
            text: `${brokenPromises} broken promise${brokenPromises === 1 ? "" : "s"}`,
            color: "#b91c1c",
        };
    } else {
        subLine = { text: "No payment history yet", color: "#9ca3af" };
    }

    const tooltip = [
        onTimePct !== null ? `${onTimePct}% paid on time` : "No settled payments yet",
        settled > 0 ? `${settled} invoice${settled === 1 ? "" : "s"} settled` : null,
        paceLabel,
        brokenPromises
            ? `${brokenPromises} broken promise${brokenPromises === 1 ? "" : "s"}`
            : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <Tooltip title={tooltip || label} arrow>
            <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 0.25 }}>
                <Chip
                    label={label}
                    size="small"
                    sx={{
                        backgroundColor: tone.bg,
                        color: tone.fg,
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: "22px",
                        width: "fit-content",
                    }}
                />
                {subLine && (
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "0.68rem",
                            color: subLine.color,
                            fontWeight: 600,
                        }}
                    >
                        {subLine.text}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
}

const KpiDetailModal: React.FC<Props> = ({
    metric,
    businessId,
    range,
    currency = "USD",
    onClose,
}) => {
    const navigate = useNavigate();
    const [data, setData] = useState<IKpiBreakdown | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const open = Boolean(metric);

    useEffect(() => {
        if (!metric || !businessId) return;
        let cancelled = false;
        setLoading(true);
        setError("");
        setData(null);
        submitGetMetricBreakdown(businessId, metric, 100, range)
            .then((res) => {
                if (!cancelled) setData(res);
            })
            .catch((err: any) => {
                if (!cancelled) setError(err?.message || "Couldn't load details.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [metric, businessId, range]);

    const meta = metric ? METRIC_META[metric] : null;
    const resolvedCurrency = data?.currency || currency;
    const items = data?.items ?? [];

    const goToInvoice = (item: IKpiBreakdownItem) => {
        if (!item.invoiceId) return;
        onClose();
        navigate(`/collect/invoices/${item.invoiceId}`);
    };

    // ── Summary stat strip, tailored per metric ──────────────────────────
    const summaryStats = useMemo(() => {
        if (!data || !metric) return [];
        const s = data.summary;
        const stats: Array<{ label: string; value: string; tone?: string }> = [];

        if (metric === "outstanding") {
            stats.push({
                label: "Total outstanding",
                value: formatCurrency(s.totalAmount, resolvedCurrency),
            });
            stats.push({
                label: "Open invoices",
                value: String(s.count),
            });
            if (typeof s.overdueAmount === "number") {
                const pct =
                    s.totalAmount > 0
                        ? Math.round((s.overdueAmount / s.totalAmount) * 100)
                        : 0;
                stats.push({
                    label: "Of which overdue",
                    value: `${formatCurrency(s.overdueAmount, resolvedCurrency)} (${pct}%)`,
                    tone: pct > 40 ? "#dc2626" : "#f59e0b",
                });
            }
        } else if (metric === "overdue") {
            stats.push({
                label: "Total overdue",
                value: formatCurrency(s.totalAmount, resolvedCurrency),
                tone: "#dc2626",
            });
            stats.push({ label: "Past-due invoices", value: String(s.count) });
        } else if (metric === "collected") {
            stats.push({
                label: `Collected (${s.rangeLabel || "selected period"})`,
                value: formatCurrency(s.totalAmount, resolvedCurrency),
                tone: colors.mainSecondary,
            });
            stats.push({ label: "Invoices with payments", value: String(s.count) });
        } else if (metric === "promises") {
            stats.push({
                label: "Expected from promises",
                value: formatCurrency(s.totalAmount, resolvedCurrency),
                tone: colors.blueRibbon,
            });
            stats.push({ label: "Active promises", value: String(s.count) });
            if (s.overdueCount) {
                stats.push({
                    label: "Already past promised date",
                    value: String(s.overdueCount),
                    tone: "#dc2626",
                });
            }
        }
        return stats;
    }, [data, metric, resolvedCurrency]);

    // ── Column set per metric ────────────────────────────────────────────
    const showAction = metric !== "collected";
    const timingHeader =
        metric === "collected"
            ? "Paid"
            : metric === "promises"
                ? "Promised"
                : "Due / overdue";
    const amountHeader =
        metric === "promises"
            ? "Promised"
            : metric === "collected"
                ? "Amount"
                : "Balance";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: { borderRadius: "16px", overflow: "hidden" },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: { xs: 2, sm: 3 },
                    background: `linear-gradient(135deg, ${meta?.accent ?? colors.primary}10, #ffffff)`,
                    borderBottom: "1px solid rgba(15,23,42,0.06)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: `${meta?.accent ?? colors.primary}15`,
                        color: meta?.accent ?? colors.primary,
                        flexShrink: 0,
                    }}
                >
                    {meta?.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: colors.primary,
                        }}
                    >
                        {meta?.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "0.82rem",
                            color: "#6b7280",
                            lineHeight: 1.5,
                            mt: 0.25,
                        }}
                    >
                        {meta?.subtitle}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            {/* Summary strip */}
            {summaryStats.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: { xs: 2, sm: 4 },
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        borderBottom: "1px solid rgba(15,23,42,0.06)",
                        backgroundColor: "#fbfcfe",
                    }}
                >
                    {summaryStats.map((stat) => (
                        <Box key={stat.label}>
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    color: "#9ca3af",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                }}
                            >
                                {stat.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "1.05rem",
                                    fontWeight: 800,
                                    color: stat.tone || colors.primary,
                                    mt: 0.25,
                                }}
                            >
                                {stat.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress sx={{ color: meta?.accent ?? colors.primary }} />
                    </Box>
                ) : error ? (
                    <CollectEmptyState>
                        <CollectEmptyTitle>Couldn't load details</CollectEmptyTitle>
                        <CollectEmptyHint>{error}</CollectEmptyHint>
                    </CollectEmptyState>
                ) : items.length === 0 ? (
                    <CollectEmptyState>
                        <CollectEmptyTitle>Nothing to show here</CollectEmptyTitle>
                        <CollectEmptyHint>
                            {metric === "collected"
                                ? `No QuickBooks payments found in ${data?.summary.rangeLabel || "this period"}.`
                                : metric === "promises"
                                    ? "No active promise-to-pay commitments right now."
                                    : "No matching invoices right now — run a sync to refresh from QuickBooks."}
                        </CollectEmptyHint>
                    </CollectEmptyState>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 760 }}>
                            <TableHead>
                                <TableRow>
                                    <CollectTableHeadCell>
                                        Customer / Invoice
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        {amountHeader}
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell>
                                        {timingHeader}
                                    </CollectTableHeadCell>
                                    {showAction && (
                                        <CollectTableHeadCell>
                                            Current action
                                        </CollectTableHeadCell>
                                    )}
                                    <CollectTableHeadCell>
                                        Payer history
                                    </CollectTableHeadCell>
                                    <CollectTableHeadCell align="center">
                                        {""}
                                    </CollectTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    const stateColors = recoveryStateColors(
                                        item.recoveryState,
                                    );
                                    const overdueDays = daysOverdueFrom(
                                        item.dueDate,
                                        item.daysOverdue,
                                    );
                                    const clickable = Boolean(item.invoiceId);
                                    return (
                                        <TableRow
                                            key={item.promiseId || item.invoiceId}
                                            hover={clickable}
                                            onClick={() => goToInvoice(item)}
                                            sx={{
                                                cursor: clickable ? "pointer" : "default",
                                                "&:last-child td": { borderBottom: 0 },
                                            }}
                                        >
                                            {/* Customer / invoice */}
                                            <CollectTableBodyCell>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT,
                                                        fontSize: "0.88rem",
                                                        fontWeight: 700,
                                                        color: colors.primary,
                                                    }}
                                                >
                                                    {item.customerDisplayName ||
                                                        "Unknown customer"}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT,
                                                        fontSize: "0.74rem",
                                                        color: "#9ca3af",
                                                    }}
                                                >
                                                    {item.docNumber
                                                        ? `Invoice #${item.docNumber}`
                                                        : "—"}
                                                    {item.brokenPromiseCount
                                                        ? ` · ${item.brokenPromiseCount} broken promise${item.brokenPromiseCount === 1 ? "" : "s"}`
                                                        : ""}
                                                    {item.disputeOpen ? " · disputed" : ""}
                                                </Typography>
                                            </CollectTableBodyCell>

                                            {/* Amount */}
                                            <CollectTableBodyCell align="right">
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT,
                                                        fontSize: "0.9rem",
                                                        fontWeight: 800,
                                                        color:
                                                            metric === "collected"
                                                                ? colors.mainSecondary
                                                                : colors.primary,
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        item.amount,
                                                        item.currency || resolvedCurrency,
                                                    )}
                                                </Typography>
                                            </CollectTableBodyCell>

                                            {/* Timing */}
                                            <CollectTableBodyCell>
                                                {metric === "collected" ? (
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <CheckCircleOutline
                                                            sx={{
                                                                fontSize: 15,
                                                                color: colors.mainSecondary,
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT,
                                                                fontSize: "0.82rem",
                                                                color: "#374151",
                                                            }}
                                                        >
                                                            {formatDate(item.recoveredAt)}
                                                        </Typography>
                                                    </Box>
                                                ) : metric === "promises" ? (
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT,
                                                                fontSize: "0.82rem",
                                                                color: "#374151",
                                                            }}
                                                        >
                                                            {formatDate(item.promisedDate)}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT,
                                                                fontSize: "0.7rem",
                                                                fontWeight: 700,
                                                                color: item.promiseOverdue
                                                                    ? "#dc2626"
                                                                    : colors.blueRibbon,
                                                            }}
                                                        >
                                                            {item.promiseOverdue
                                                                ? "Past due"
                                                                : "Upcoming"}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT,
                                                                fontSize: "0.82rem",
                                                                color: "#374151",
                                                            }}
                                                        >
                                                            {formatDate(item.dueDate)}
                                                        </Typography>
                                                        {overdueDays > 0 && (
                                                            <Box
                                                                sx={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: 0.35,
                                                                }}
                                                            >
                                                                <WarningAmber
                                                                    sx={{
                                                                        fontSize: 12,
                                                                        color: "#dc2626",
                                                                    }}
                                                                />
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: FONT,
                                                                        fontSize: "0.7rem",
                                                                        fontWeight: 700,
                                                                        color: "#dc2626",
                                                                    }}
                                                                >
                                                                    {overdueDays}d overdue
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </CollectTableBodyCell>

                                            {/* Current action (recovery state) */}
                                            {showAction && (
                                                <CollectTableBodyCell>
                                                    {item.recoveryState ? (
                                                        <Chip
                                                            label={recoveryStateLabel(
                                                                item.recoveryState,
                                                            )}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: stateColors.bg,
                                                                color: stateColors.fg,
                                                                border: `1px solid ${stateColors.border}`,
                                                                fontFamily: FONT,
                                                                fontWeight: 700,
                                                                fontSize: "0.68rem",
                                                                height: "22px",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT,
                                                                fontSize: "0.78rem",
                                                                color: "#9ca3af",
                                                            }}
                                                        >
                                                            —
                                                        </Typography>
                                                    )}
                                                </CollectTableBodyCell>
                                            )}

                                            {/* Payer history */}
                                            <CollectTableBodyCell>
                                                <PayerHistoryCell payer={item.payerHistory} />
                                            </CollectTableBodyCell>

                                            {/* Open chevron */}
                                            <CollectTableBodyCell align="center">
                                                {clickable && (
                                                    <Tooltip title="Open invoice" arrow>
                                                        <NorthEast
                                                            sx={{
                                                                fontSize: 16,
                                                                color: "#9ca3af",
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {data && data.summary.count > items.length && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.75,
                                    py: 2,
                                    borderTop: "1px solid rgba(15,23,42,0.06)",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: FONT,
                                        fontSize: "0.8rem",
                                        color: "#6b7280",
                                    }}
                                >
                                    Showing top {items.length} of {data.summary.count}.
                                </Typography>
                                <Box
                                    onClick={() => {
                                        onClose();
                                        navigate("/collect/invoices");
                                    }}
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.4,
                                        cursor: "pointer",
                                        color: colors.blueRibbon,
                                        fontFamily: FONT,
                                        fontSize: "0.8rem",
                                        fontWeight: 700,
                                    }}
                                >
                                    View all invoices
                                    <OpenInNew sx={{ fontSize: 14 }} />
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default KpiDetailModal;
